import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate  } from 'react-router-dom';

import AlertModal from '../AlertModal';
import RegistrationModal from '../RegistrationModal';

import { defaultAlertConfig } from '../../utils/constants/general';
import { onAuthStateChanged } from 'firebase/auth';
import auth, { hasDisplayName, isEmailVerified } from '../../utils/firebase/auth';
import { Card, Spinner } from 'reactstrap';
import MainCardHeader from '../MainCardHeader';
import Footer from '../Footer';
import CongratulationsContent from '../RoleRegistration/CongratulationsContent';
import { BETA_ROLES } from '../../utils/constants/betaRoles';
import RegistrationContent from '../RoleRegistration/RegistrationContent';
import ExistingRoleContent from '../RoleRegistration/ExistingRoleContent';

function RoutePage({ isFullHeight = false, ContentRenderer }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isVerification, setIsVerification] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ ...defaultAlertConfig });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userBetaRoleId, setUserBetaRoleId] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState('idle');
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);

  const showAlert = (message = '', isConfirm = false) => {
    if (message) {
        setAlertConfig({
            isOpen: true,
            isConfirm,
            message,
        });
    }
  };

  const hideAlert = () => {
    setAlertConfig({ ...defaultAlertConfig })
  };

  const closeModal = () => {
      setIsModalOpen(false);
  }

  const handleConfirmSuccess = event => {
      navigate('/verified');
  };

  const gotoRolesPage = () => {
    closeModal();
    navigate('/beta-roles');
  }

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
  
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
  
    // Default behavior: scroll to top on route change
    window.scrollTo({ top: 0, behavior: 'instant' });  
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setIsAuthLoading(false);

      if (user) {
        const params = new URLSearchParams(window.location.search);
        
        if (params.has('verified') && params.get('verified') === 'true') {
            const isUserVerified = isEmailVerified();
            const userHasDisplayName = hasDisplayName();

            setIsVerification(true);
            if (isUserVerified && userHasDisplayName) {
              setIsUserAuthenticated(true);
              setIsModalOpen(true);
            }
        }
      }
    });

    return () => unsubscribe();
  }, []);


  if (isAuthLoading) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center gap-2 text-secondary vh-100">
          <Spinner className="text-primary-orange" />
        </div>
      );
  }

  return (
    <main className={`page-shell ${isFullHeight ? 'page-shell--full' : ''}`}>
      <div className="page-shell__inner container-fluid px-lg-4">
        <div className="page-stack">
          <AlertModal
              alertConfig={alertConfig}
              handleConfirmSuccess={handleConfirmSuccess}
              handleCloseOnClick={hideAlert}
          />

          <RegistrationModal
              isOpen={isModalOpen}
              closeModal={closeModal}
          >
            {isUserAuthenticated ? (
              <>
                {userBetaRoleId && !(isVerification || isSubmissionSuccessful) ? (
                  <ExistingRoleContent
                    userRoleTitle={BETA_ROLES.find(({ id }) => id === userBetaRoleId)?.title ?? ''}
                    discordInviteCode={BETA_ROLES.find(({ id }) => id === userBetaRoleId)?.discordInviteCode ?? ''}
                  />
                ) : (
                  <>
                    {(isVerification || isSubmissionSuccessful) && (
                      <CongratulationsContent 
                        isBetaRolesPage={location.pathname.includes('beta-roles')}
                        roleData={BETA_ROLES.find(role => role.id === currentRoleId) ?? {}}
                        gotoRolesPage={gotoRolesPage}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <RegistrationContent showAlert={showAlert} closeModal={closeModal} />
            )}
          </RegistrationModal>

          <section className="position-fixed fixed-top p-0 surface-elevated overflow-hidden">
              <Card className="border-0 bg-transparent shadow-none m-0 p-0">
                  <MainCardHeader />
              </Card>
          </section>

          <ContentRenderer
            userBetaRoleId={userBetaRoleId}
            setUserBetaRoleId={setUserBetaRoleId}
            submissionStatus={submissionStatus}
            setSubmissionStatus={setSubmissionStatus}
            isUserAuthenticated={isUserAuthenticated}
            setIsUserAuthenticated={setIsUserAuthenticated}
            currentRoleId={currentRoleId}
            setCurrentRoleId={setCurrentRoleId}
            isSubmissionSuccessful={isSubmissionSuccessful}
            setIsSubmissionSuccessful={setIsSubmissionSuccessful}
            showAlert={showAlert}
            setIsModalOpen={setIsModalOpen}
          />

          <Footer />
        </div>
      </div>
    </main>
  );
}

export default RoutePage;
